/**
 * cl-styles.js
 * Shared style tokens, MUI sx objects, and shared components for the Checklists module.
 * Edit here to change the look across all CL pages at once.
 */
import React, { memo } from "react";
// Onsen UI Toolbar is kept as the page shell (required for scroll/safe-area on mobile); MUI is used for all content — intentional hybrid
import { Toolbar } from "react-onsenui";
import { Typography } from "@mui/material";
import { Icon } from "@iconify/react";
import { useDate } from "../useDate";

// ── Palette ────────────────────────────────────────────────────────────────
export const CL_PRIMARY   = '#1e88e5';
export const CL_PAGE_BG   = '#f4f6f9';
export const CL_CARD_BG   = '#ffffff';
export const CL_BORDER    = 'rgba(0,0,0,0.09)';
export const CL_MUTED     = 'rgba(0,0,0,0.38)';
export const CL_SHADOW    = '0 1px 4px rgba(0,0,0,0.07)';

// ── Onsen Page ─────────────────────────────────────────────────────────────
export const clPageStyle = { backgroundColor: CL_PAGE_BG };

// ── Page content wrapper ────────────────────────────────────────────────────
export const clContentBox = { px: 2, pt: 1.5, pb: 10 };

// ── Cards / list items ──────────────────────────────────────────────────────
export const clCardSx = {
    borderRadius: 2,
    border: `1px solid ${CL_BORDER}`,
    backgroundColor: CL_CARD_BG,
    overflow: 'hidden',
    boxShadow: CL_SHADOW,
};

export const clCardListSx = { display: 'flex', flexDirection: 'column', gap: 0.4 };

export const clItemButtonSx = { px: 2, py: 0.75 };

export const clItemIconSx = { minWidth: 34 };

// ── Loading / empty states ──────────────────────────────────────────────────
export const clCentredState = {
    display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh',
};

// ── Dashboard nav list ──────────────────────────────────────────────────────
export const clNavListStyle = {
    marginTop: 8, borderRadius: 10, overflow: 'hidden',
    border: `1px solid ${CL_BORDER}`, boxShadow: CL_SHADOW,
};

export const clNavRowStyle = (isLast) => ({
    display: 'flex', alignItems: 'center', padding: '13px 16px',
    backgroundColor: CL_CARD_BG,
    borderBottom: isLast ? 'none' : `1px solid ${CL_BORDER}`,
});

// ── Shared inner-page header ────────────────────────────────────────────────
/**
 * ClPageHeader — minimal bar: back arrow left, live time right.
 * Page title is displayed in the content area as a bold heading, not in the toolbar.
 *
 * Usage:
 *   <Page renderToolbar={() => <ClPageHeader onBack={goBack} />}>
 *     <Box sx={clContentBox}>
 *       <ClPageTitle title="My Checklists" subtitle="Select a location to start" />
 *       ...
 *     </Box>
 *   </Page>
 */
// memo() ensures parent CL pages do NOT re-render on every clock tick.
// ClPageHeader itself re-renders every second (useDate interval) but is isolated — only the toolbar node updates.
export const ClPageHeader = memo(({ onBack }) => {
    const time = useDate();
    return (
        <Toolbar style={{
            backgroundColor: '#ffffff',
            borderBottom: '1px solid rgba(0,0,0,0.07)',
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        }}>
            <div className="left" style={{ display: 'flex', alignItems: 'center', paddingLeft: 4 }}>
                <button
                    type="button"
                    onClick={onBack}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 2,
                        background: 'none', border: 'none', cursor: 'pointer',
                        padding: '6px 8px', borderRadius: 8, color: '#1a1a1a',
                    }}
                >
                    <Icon icon="mdi:chevron-left" style={{ fontSize: 22, color: CL_PRIMARY }} />
                    <span style={{ fontSize: '0.9rem', fontWeight: 600, color: CL_PRIMARY, lineHeight: 1 }}>Back</span>
                </button>
            </div>
            <div className="center" />
            <div className="right" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 12, minWidth: 90 }}>
                <Typography variant="caption" sx={{ color: '#777', fontWeight: 500, whiteSpace: 'nowrap' }}>{time}</Typography>
            </div>
        </Toolbar>
    );
});

/**
 * ClPageTitle — bold page heading + optional subtitle rendered in content area.
 */
export const ClPageTitle = ({ title, subtitle }) => (
    <div style={{ marginBottom: 16 }}>
        <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2, color: '#1a1a1a', letterSpacing: '-0.02em' }}>
            {title}
        </Typography>
        {subtitle && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                {subtitle}
            </Typography>
        )}
    </div>
);
